import { useEffect, useRef, useState } from 'react';
import { Alert, App, Input, Modal, Typography } from 'antd';

import { attest } from '../../services/mfa';

/**
 * Signing a record: the small panel a save opens when the card requires a
 * signature (`options.record.attest`).
 *
 * Deliberately one field and a numeric keypad. This appears on a med cart
 * in a hallway, a terminal on a shop floor, a till between two shifts — the
 * only thing it may cost is four digits and a second, or people will find a
 * way around it and the signature will mean nothing.
 *
 * The PIN never leaves as anything but a PIN: it is exchanged for a
 * short-lived token bound to this card, which the save then carries. Nothing
 * here can mint a session, which is what makes four digits an acceptable
 * secret in the first place.
 *
 * One act that writes SEVERAL records — a multi add submit, a med pass —
 * is signed once (KPR-1725): `count` says how many records the signature goes
 * on, and `cardIds` (two or more signing cards) mints one token for all of
 * them.
 */

const { Text } = Typography;

export default function AttestationModal({ open, mode = 'pin', collectionId, cardId, cardIds, count = 1, title, onClose, onSigned }) {

	const { message } = App.useApp();
	const [ secret, setSecret ] = useState('');
	const [ saving, setSaving ] = useState(false);
	// One signature at a time: a second Enter while the first is being minted
	// would mint a second token, or count a wrong PIN twice toward the lockout.
	const inFlight = useRef(false);
	const several = Number(count) > 1;

	useEffect(() => { if (open) setSecret(''); }, [ open ]);

	const submit = async () => {
		if (!secret || inFlight.current) return;
		inFlight.current = true;
		setSaving(true);
		try {
			const result = await attest({
				...(mode === 'password' ? { password: secret } : { pin: secret }),
				collectionId,
				cardId,
				cardIds,
			});
			// Signed: `onSigned` alone. Every parent closes the dialog in it. A
			// trailing onClose here read as a CANCEL in each of them, failing the
			// very save the signature was for while it went on to land (KPR-1754,
			// found in KPR-1725).
			onSigned?.(result.attestation);
		}
		catch (err) {
			const code = err?.response?.data?.code;
			message.error(code === 'attestation_locked'
				? 'Too many wrong attempts. Wait a few minutes, or sign with your password.'
				: (err?.response?.data?.message || 'That is not right.'));
			setSecret('');
		}
		finally {
			inFlight.current = false;
			setSaving(false);
		}
	};

	return (
		<Modal
			open={open}
			onCancel={onClose}
			onOk={submit}
			okText='Sign'
			okButtonProps={{ disabled: !secret }}
			confirmLoading={saving}
			title={title || (several ? `Sign these ${count} records` : 'Sign this record')}
			width={380}
			destroyOnClose
		>
			<Alert
				type='info'
				showIcon
				style={{ marginBottom: 12 }}
				message={several ? `Your name goes on all ${count} entries` : 'Your name goes on this entry'}
				description='Signing records who performed it — not just whose session is open on this device.'
			/>
			{mode === 'password' ? (
				<Input.Password
					value={secret}
					autoFocus
					autoComplete='current-password'
					aria-label='Your password'
					placeholder='Your password'
					onChange={event => setSecret(event.target.value)}
					onPressEnter={submit}
				/>
			) : (
				<Input
					value={secret}
					autoFocus
					inputMode='numeric'
					maxLength={8}
					aria-label='Your PIN'
					placeholder='Your PIN'
					onChange={event => setSecret(event.target.value.replace(/\D/g, ''))}
					onPressEnter={submit}
				/>
			)}
			<Text type='secondary' className='d-block' style={{ marginTop: 8 }}>
				Set or change your PIN in your profile.
			</Text>
		</Modal>
	);
}
